// React
import PropTypes from "prop-types";
import { act, useContext, useEffect, useState } from "react";

// @mui material components
import {
  Autocomplete,
  Card,
  Checkbox,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import Vehiculos from "../../../declaracion/Vehiculos";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
export default function ModalEmpresa({ open, handleClose, refetch, idEmpresa }) {
  const [tab, setTab] = useState(0);
  const [empresa, setEmpresa] = useState({});
  const [actividades, setActividades] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const { handleSnackbar } = useContext(MessageManager);
  const esTransportista = actividades.includes("Transportista");

  useEffect(() => {
    if (open) setTab(0);
  }, [open]);

  const handleChanges = (e) => {
    setEmpresa({
      ...empresa,
      [e.target.name]: e.target.value,
    });
  };

  const [getEmpresa, { data: dataEmpresa }] = useLazyQuery(
    gql`
      query getEmpresa($id: ID!) {
        empresa(id: $id) {
          __typename @skip(if: true)
          id
          razonSocial
          cuit
          email
          actividad
          gerente
          codigoAfip
          registroCatastral
          habilitacionMunicipalNumero
          habilitacionMunicipalLugar
          calleLegal
          localidadLegal
          cpLegal
          provinciaLegal
          departamentoLegal
          telefonoLegal
          faxLegal
          calleReal
          localidadReal
          cpReal
          provinciaReal
          departamentoReal
          telefonoReal
          faxReal
          vehiculos {
            id
            tipo
            marcaModelo
            dominio
            tallerPropio
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
    }
  );
  const [saveEmpresa, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveEmpresa($input: EmpresaInput!) {
        saveEmpresa(input: $input) {
          id
        }
      }
    `
  );

  const handleSave = () => {
    const input = {
      ...empresa,
      id: empresa?.id || null,
      actividad: actividades,
    };

    if (esTransportista) {
      input.vehiculos = vehiculos
        .filter((vehiculo) => !vehiculo.aBorrar)
        .map(({ id, tipo, marcaModelo, dominio, tallerPropio }) => ({
          id,
          tipo,
          marcaModelo,
          dominio,
          tallerPropio,
        }));
    }

    saveEmpresa({
      variables: {
        input,
      },
    })
      .then(() => {
        refetch();
        handleSnackbar("Empresa guardada correctamente", "success");
        handleClose();
      })
      .catch((e) => {
        handleSnackbar(e.message || "Error al guardar la empresa", "error");
      });
  };
  useEffect(() => {
    if (idEmpresa && open) {
      getEmpresa({ variables: { id: idEmpresa } })
        .then((res) => {
          setActividades(res?.data?.empresa?.actividad || []);
        })
        .catch((e) => {
          handleSnackbar(e.message || "Error al obtener la empresa", "error");
        });
    } else if (open) {
      setActividades([]);
      setVehiculos([]);
    }
  }, [idEmpresa, open]);

  useEffect(() => {
    if (dataEmpresa && idEmpresa && open) {
      setEmpresa(dataEmpresa.empresa);
      setVehiculos(dataEmpresa.empresa.vehiculos || []);
    } else {
      setEmpresa({});
      setVehiculos([]);
    }
  }, [dataEmpresa, idEmpresa, open]);

  useEffect(() => {
    if (!esTransportista && tab === 3) setTab(0);
  }, [esTransportista, tab]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="h6">
                {empresa?.id ? "Editar Empresa" : "Agregar Empresa"}
              </SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              variant="fullWidth"
              aria-label="Datos de la empresa"
              sx={{ mt: 2 }}
            >
              {[
                "Datos generales",
                "Domicilio legal",
                "Domicilio real",
                ...(esTransportista ? ["Vehículos"] : []),
              ].map((label, index) => (
                <Tab
                  key={label}
                  label={label}
                  id={`empresa-tab-${index}`}
                  aria-controls={`empresa-panel-${index}`}
                />
              ))}
            </Tabs>
            <SoftBox py={3}>
              <div
                role="tabpanel"
                id="empresa-panel-0"
                aria-labelledby="empresa-tab-0"
                hidden={tab !== 0}
              >
                <Grid container spacing={2}>
                  {/* Existing fields */}
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="razonSocial">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Razón Social
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="razonSocial"
                      placeholder="Ingrese la razón social"
                      type="text"
                      name="razonSocial"
                      value={empresa?.razonSocial || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="cuit">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        CUIT
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="cuit"
                      placeholder="Ingrese el CUIT"
                      type="number"
                      name="cuit"
                      value={empresa?.cuit || ""}
                      onChange={handleChanges}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="email">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Email
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="email"
                      placeholder="Ingrese el email"
                      type="email"
                      name="email"
                      value={empresa?.email || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="actividad">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Actividad
                      </SoftTypography>
                    </InputLabel>
                    <Autocomplete
                      multiple
                      id="actividad"
                      size="small"
                      options={[
                        "Generadora",
                        "Generador Comercio/Servicio",
                        // "Generadora/Operadora",
                        "Transportista",
                        "Operadora",
                        "Operador in situ",
                        "Recicladora/Operadora",
                        // "Recicladora",
                      ]}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      value={actividades} // Usa el estado `actividades` directamente
                      onChange={(event, newValue) => {
                        // Actualiza el estado eliminando o agregando valores según corresponda
                        const updatedActividades = newValue.filter(
                          (item, index, self) => self.findIndex((t) => t === item) === index
                        );
                        setActividades(updatedActividades);
                      }}
                      renderOption={(props, option, { selected }) => {
                        return (
                          <li {...props}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={actividades.some((actividad) => actividad === option)}
                            />
                            {option}
                          </li>
                        );
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="gerente">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Gerente
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="gerente"
                      placeholder="Ingrese el nombre del gerente"
                      type="text"
                      name="gerente"
                      value={empresa?.gerente || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="codigoAfip">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Código AFIP
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="codigoAfip"
                      placeholder="Ingrese el código AFIP"
                      type="number"
                      name="codigoAfip"
                      value={empresa?.codigoAfip || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="registroCatastral">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Registro Catastral
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="registroCatastral"
                      placeholder="Ingrese el registro catastral"
                      type="number"
                      name="registroCatastral"
                      value={empresa?.registroCatastral || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="habilitacionMunicipalNumero">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Habilitación Municipal Número
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="habilitacionMunicipalNumero"
                      placeholder="Ingrese el número de habilitación municipal"
                      type="number"
                      name="habilitacionMunicipalNumero"
                      value={empresa?.habilitacionMunicipalNumero || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="habilitacionMunicipalLugar">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Habilitación Municipal Lugar
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="habilitacionMunicipalLugar"
                      placeholder="Ingrese el lugar de habilitación municipal"
                      type="text"
                      name="habilitacionMunicipalLugar"
                      value={empresa?.habilitacionMunicipalLugar || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                </Grid>
              </div>
              {["Legal", "Real"].map((domicilio, index) => (
                <div
                  key={domicilio}
                  role="tabpanel"
                  id={`empresa-panel-${index + 1}`}
                  aria-labelledby={`empresa-tab-${index + 1}`}
                  hidden={tab !== index + 1}
                >
                  <Grid container spacing={2}>
                    {[
                      ["calle", "Calle"],
                      ["localidad", "Localidad"],
                      ["cp", "Código postal"],
                      ["provincia", "Provincia"],
                      ["departamento", "Departamento"],
                      ["telefono", "Teléfono"],
                      ["fax", "Fax"],
                    ].map(([campo, etiqueta]) => {
                      const name = `${campo}${domicilio}`;
                      return (
                        <Grid item xs={12} sm={4} key={name}>
                          <InputLabel htmlFor={name}>
                            <SoftTypography component="span" variant="caption" fontWeight="bold">
                              {etiqueta}
                            </SoftTypography>
                          </InputLabel>
                          <SoftInput
                            id={name}
                            name={name}
                            type={campo === "telefono" || campo === "fax" ? "tel" : "text"}
                            value={empresa?.[name] || ""}
                            onChange={handleChanges}
                            fullWidth
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </div>
              ))}
              {esTransportista && (
                <div
                  role="tabpanel"
                  id="empresa-panel-3"
                  aria-labelledby="empresa-tab-3"
                  hidden={tab !== 3}
                >
                  <Vehiculos
                    listadoVehiculos={vehiculos}
                    setListadoVehiculos={setVehiculos}
                    finalizada={false}
                    idEmpresa={empresa?.id}
                    mostrarAnteriores={false}
                  />
                </div>
              )}
            </SoftBox>
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
              <SoftButton color="primary" onClick={handleClose} sx={{ marginRight: 1 }}>
                <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                &nbsp;Cancelar
              </SoftButton>
              <SoftButton
                color="gobSecondary"
                disabled={loadingSave || !empresa?.razonSocial || !empresa?.cuit || !empresa?.email}
                onClick={handleSave}
              >
                {loadingSave ? (
                  <CircularProgress size={15} sx={{ marginRight: 0.5 }} color="inherit" />
                ) : (
                  <Icon sx={{ fontWeight: "light" }}>save</Icon>
                )}
                &nbsp;Guardar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalEmpresa.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  idEmpresa: PropTypes.string,
};
